import {  t  } from "@/client/libs/dummy-i18n";
import { ArrowLeft as ArrowLeftIcon, Gear as SettingsIcon, Plus as PlusIcon, Trash as TrashIcon, Users as UsersIcon } from "@phosphor-icons/react";
import { Avatar, Badge, Button, Card, CardContent, CardDescription, CardHeader, CardTitle, Separator, Tabs, TabsContent, TabsList, TabsTrigger } from "@stackmorph/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Helmet } from "react-helmet-async";
import { useNavigate, useParams } from "react-router";

import { getOrganization, removeMember } from "@/client/services/organization/organization";
import { useUser } from "@/client/services/user/user";

import { AddMemberDialog } from "./components/add-member-dialog";
import { DeleteOrgDialog } from "./components/delete-org-dialog";

export const OrganizationDetailPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useUser();
  const [isAddMemberOpen, setIsAddMemberOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [memberToRemove, setMemberToRemove] = useState<string | null>(null);

  const { data: organization, isLoading, isError, error } = useQuery({
    queryKey: ["organization", id],
    queryFn: () => getOrganization(id!),
    enabled: !!id,
  });

  const { mutate: removeMemberMutation } = useMutation({
    mutationFn: ({ memberId }: { memberId: string }) => removeMember(id!, memberId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["organization", id] });
      setMemberToRemove(null);
    },
  });

  const handleRemoveMember = (memberId: string) => {
    if (confirm(t`Are you sure you want to remove this member?`)) {
      removeMemberMutation({ memberId });
    }
  };

  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="text-center">
          <p className="text-muted-foreground">{t`Loading organization...`}</p>
        </div>
      </div>
    );
  }

  if (isError || !organization) {
    console.error("Organization load error:", error);
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold">{t`Organization not found`}</h2>
          <p className="mt-2 text-muted-foreground">{t`The organization you're looking for doesn't exist.`}</p>
          <Button className="mt-4" onClick={() => navigate("/dashboard/team")}>
            {t`Back to Team`}
          </Button>
        </div>
      </div>
    );
  }

  const currentUserMembership = organization.users?.find((m) => m.user.id === user?.id);
  const isOwner = currentUserMembership?.role === "owner";

  return (
    <>
      <Helmet>
        <title>
          {organization.name} - {t`Team`} - {t`Stackmorph Resume Builder`}
        </title>
      </Helmet>

      <AddMemberDialog isOpen={isAddMemberOpen} setIsOpen={setIsAddMemberOpen} organizationId={id!} />
      <DeleteOrgDialog isOpen={isDeleteOpen} setIsOpen={setIsDeleteOpen} organizationId={id!} organizationName={organization.name} />

      <div className="max-w-screen-lg space-y-6 p-6">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate("/dashboard/team")}>
            <ArrowLeftIcon size={20} />
          </Button>
          <div className="flex-1">
            <h1 className="text-3xl font-bold tracking-tight">{organization.name}</h1>
            <p className="text-sm text-muted-foreground">
              {t`Created on`} {new Date(organization.createdAt).toLocaleDateString()} • {organization._count.users} {t`members`} • {organization._count.resumes} {t`resumes`}
            </p>
          </div>
          <div className="flex gap-2">
            <Button onClick={() => { setIsAddMemberOpen(true); }}>
              <PlusIcon className="mr-2" size={16} />
              {t`Add Member`}
            </Button>
            {isOwner && (
              <Button variant="ghost" size="icon" onClick={() => { setIsDeleteOpen(true); }}>
                <SettingsIcon size={20} />
              </Button>
            )}
          </div>
        </div>

        <Separator />

        {/* Tabs */}
        <Tabs defaultValue="members" className="w-full">
          <TabsList>
            <TabsTrigger value="members">
              <UsersIcon className="mr-2" size={16} />
              {t`Members`}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="members" className="mt-6">
            <div className="space-y-4">
              {organization.users?.length === 0 ? (
                <Card>
                  <CardContent className="py-12 text-center">
                    <UsersIcon size={48} className="mx-auto mb-4 opacity-50" />
                    <h3 className="text-lg font-medium">{t`No members yet`}</h3>
                    <p className="text-muted-foreground">{t`Invite members to collaborate on resumes.`}</p>
                  </CardContent>
                </Card>
              ) : (
                <div className="grid gap-4">
                  {organization.users?.map((member) => (
                    <Card key={member.id}>
                      <CardHeader>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-4">
                            <Avatar>
                              {member.user.picture ? (
                                <img src={member.user.picture} alt={member.user.name} />
                              ) : (
                                <div className="flex h-full w-full items-center justify-center bg-primary/10 text-primary">
                                  {member.user.name.charAt(0).toUpperCase()}
                                </div>
                              )}
                            </Avatar>
                            <div>
                              <CardTitle className="text-base">{member.user.name}</CardTitle>
                              <CardDescription>{member.user.email}</CardDescription>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <Badge variant={member.role === "owner" ? "primary" : "secondary"}>
                              {member.role === "owner" ? t`Owner` : t`Member`}
                            </Badge>
                            {isOwner && member.role !== "owner" && (
                              <Button
                                variant="ghost"
                                size="icon"
                                className="text-error"
                                onClick={() => { handleRemoveMember(member.user.id); }}
                              >
                                <TrashIcon size={16} />
                              </Button>
                            )}
                          </div>
                        </div>
                      </CardHeader>
                    </Card>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </>
  );
};
